import React from 'react';
import {
  Box, Button, Grid, Stack, Typography,
} from '@mui/material';
import { Trans } from '@gamiphy/react-i18n';
import { observer } from 'mobx-react';
import { AppCashbackStore } from '@gamiphy/loyalty-station-desktop/store/appCashback';
import profits from '@gamiphy/loyalty-station-desktop/components/cashback/assets/profits.svg';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useOnCopy } from '@gamiphy/utils/react';
import { DiscountStatuses } from '@gamiphy/bl/cashbackDiscount';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { ThemeStore } from '@gamiphy/loyalty-station-desktop/store/theme';
import { CashbackPopupsStore } from '@gamiphy/loyalty-station-desktop/store/cashbackPopups';
import { CashbackSummaryResponse } from '@coretava/api/ecommerce-service';
import { formatCashbackPromotionValue, formatDiscountValue } from '../../../utils';

const Discount: React.FC<{ discountApplicable: boolean, bestFitDiscount?: CashbackSummaryResponse }> = ({
  discountApplicable,
  bestFitDiscount,
}) => {
  const discountCode = React.useMemo(() => {
    if (discountApplicable && AppStore.app.agent !== 'vtex') {
      return bestFitDiscount?.applicableDiscount.discount?.code;
    }

    return undefined;
  }, [discountApplicable, bestFitDiscount]);

  const title = React.useMemo(() => {
    switch (true) {
      case !!bestFitDiscount?.applicableDiscount.discount && bestFitDiscount?.applicableDiscount.discount.status === DiscountStatuses.Valid:
        return (
          <Trans
            i18nKey="cashbackPopup.discountAwaits"
            defaults="Congrats!<br />{{discount}} discount awaits you!"
            values={{
              discount: formatDiscountValue(bestFitDiscount.applicableDiscount.discount),
            }}
          />
        );
      case AppStore.app.agent === 'wake':
        return (
          <Trans
            i18nKey="cashbackPopup.cashbackAwaits"
            defaults="Congrats!<br />{{cashback}} cashback awaits you!"
            values={{
              cashback: AppCashbackStore.highestPromotionAppCashback && formatCashbackPromotionValue(AppCashbackStore.highestPromotionAppCashback),
            }}
          />
        );
      default:
        return (
          <Trans
            i18nKey="cashbackPopup.cashbackAwaits"
            defaults="Congrats!<br />{{cashback}} cashback awaits you!"
            values={{
              cashback: `${AppCashbackStore.currentAppCashback?.percentage}%`,
            }}
          />
        );
    }
  }, [
    bestFitDiscount,
    AppCashbackStore.defaultAppCashback,
    AppCashbackStore.currentAppCashback,
    AppCashbackStore.highestPromotionAppCashback,
    AppStore.app,
  ]);

  const description = React.useMemo(() => {
    switch (true) {
      case discountApplicable:
        if (AppStore.app.agent === 'wake') {
          return (
            <Trans
              i18nKey="cashbackPopup.purchase.finalize.predefinedPromotions"
              defaults="Finalize your purchase and enjoy the discount you’ve unlocked. Plus, earn up to {{nextCashback}} cashback!"
              values={{
                nextCashback: AppCashbackStore.highestPromotionAppCashback && formatCashbackPromotionValue(AppCashbackStore.highestPromotionAppCashback),
              }}
            />
          );
        }
        if (AppCashbackStore.currentAppCashback?.enabled) {
          return (
            <Trans
              i18nKey="cashbackPopup.purchase.finalize"
              defaults="Finalize your purchase and enjoy the discount you’ve unlocked. Plus, get an additional {{cashbackPercentage}}% cashback!"
              values={{
                cashbackPercentage: AppCashbackStore.currentAppCashback.percentage,
              }}
            />
          );
        }
        return (
          <Trans
            i18nKey="cashbackPopup.purchase.finalizeWithoutCashback"
            defaults="Finalize your purchase and enjoy the discount you’ve unlocked."
          />
        );

      case !!bestFitDiscount?.applicableDiscount.discount && bestFitDiscount?.applicableDiscount.discount.status === DiscountStatuses.Valid:
        if (AppStore.app.agent === 'wake') {
          return (
            <Trans
              i18nKey="cashbackPopup.purchase.noFit.make.predefinedPromotions"
              defaults="Make a purchase over {{purchaseValue, currency}} to enjoy your unlocked discount and earn up to {{nextCashback}} additional cashback!"
              values={{
                purchaseValue: bestFitDiscount?.applicableDiscount.discount?.cashbackConfig.minRedemptionValue,
                nextCashback: AppCashbackStore.highestPromotionAppCashback && formatCashbackPromotionValue(AppCashbackStore.highestPromotionAppCashback),
              }}
            />
          );
        }
        if (bestFitDiscount?.applicableDiscount.discount.cashbackConfig.multiplier === 0) {
          if (AppCashbackStore.currentAppCashback?.enabled) {
            return (
              <Trans
                i18nKey="cashbackPopup.purchase.noFit.make.zeroMultiplier"
                defaults="Make a purchase and enjoy the discount you've unlocked. Plus, get an additional {{cashbackPercentage}}% cashback!"
                values={{
                  cashbackPercentage: AppCashbackStore.currentAppCashback.percentage,
                }}
              />
            );
          }
          return (
            <Trans
              i18nKey="cashbackPopup.purchase.noFit.make.zeroMultiplierWithoutCashback"
              defaults="Make a purchase and enjoy the discount you've unlocked."
            />
          );
        }
        if (bestFitDiscount?.applicableDiscount.discount.cashbackConfig.partialRedemptionEnabled) {
          if (AppCashbackStore.currentAppCashback?.enabled) {
            return (
              <Trans
                i18nKey="cashbackPopup.purchase.noFit.make.partialRedemption"
                defaults="Make a new purchase and enjoy the discount you've unlocked. Plus, get an additional {{cashbackPercentage}}% cashback!"
                values={{
                  cashbackPercentage: AppCashbackStore.currentAppCashback.percentage,
                }}
              />
            );
          }
          return (
            <Trans
              i18nKey="cashbackPopup.purchase.noFit.make.partialRedemptionWithoutCashback"
              defaults="Make a new purchase and enjoy the discount you've unlocked."
            />
          );
        }
        if (AppCashbackStore.currentAppCashback?.enabled) {
          return (
            <Trans
              i18nKey="cashbackPopup.purchase.noFit.make"
              defaults="Make a purchase over {{purchaseValue, currency}} and enjoy the discount you've unlocked. Plus, get an additional {{cashbackPercentage}}% cashback!"
              values={{
                purchaseValue: bestFitDiscount?.applicableDiscount.discount?.cashbackConfig.minRedemptionValue,
                cashbackPercentage: AppCashbackStore.currentAppCashback.percentage,
              }}
            />
          );
        }
        return (
          <Trans
            i18nKey="cashbackPopup.purchase.noFit.makeWithoutCashback"
            defaults="Make a purchase over {{purchaseValue, currency}} and enjoy the discount you've unlocked."
            values={{
              purchaseValue: bestFitDiscount?.applicableDiscount.discount?.cashbackConfig.minRedemptionValue,
            }}
          />
        );
      default:
        if (AppStore.app.agent === 'wake') {
          return (
            <Trans
              i18nKey="cashbackPopup.purchase.make.predefinedPromotions"
              defaults="Make a purchase over {{purchaseValue, currency}} and get up to {{cashback}} cashback."
              values={{
                purchaseValue: AppCashbackStore.lowestPromotionAppCashback?.minPurchaseValue,
                cashback: AppCashbackStore.highestPromotionAppCashback && formatCashbackPromotionValue(AppCashbackStore.highestPromotionAppCashback),
              }}
            />
          );
        }
        if (!!bestFitDiscount?.applicableDiscount.discount && bestFitDiscount?.applicableDiscount.discount.cashbackConfig.multiplier === 0) {
          if (AppCashbackStore.currentAppCashback?.enabled) {
            return (
              <Trans
                i18nKey="cashbackPopup.purchase.noFit.make.zeroMultiplier"
                defaults="Make a purchase and enjoy the discount you've unlocked. Plus, get an additional {{cashbackPercentage}}% cashback!"
                values={{
                  cashbackPercentage: AppCashbackStore.currentAppCashback.percentage,
                }}
              />
            );
          }
          return (
            <Trans
              i18nKey="cashbackPopup.purchase.noFit.make.zeroMultiplierWithoutCashback"
              defaults="Make a purchase and enjoy the discount you've unlocked."
            />
          );
        }
        return (
          <Trans
            i18nKey="cashbackPopup.purchase.make"
            defaults="Make a purchase over {{purchaseValue, currency}} and get {{percentage}}% cashback."
            values={{
              percentage: AppCashbackStore.currentAppCashback?.percentage,
              purchaseValue: AppCashbackStore.defaultAppCashback?.minPurchaseValue,
            }}
          />
        );
    }
  }, [
    discountApplicable,
    bestFitDiscount,
    AppCashbackStore.defaultAppCashback,
    AppCashbackStore.currentAppCashback,
    AppCashbackStore.highestPromotionAppCashback,
    AppStore.app,
    AppCashbackStore.lowestPromotionAppCashback,
  ]);

  const {
    copied,
    onCopy,
  } = useOnCopy(discountCode);

  return (
    <Stack alignItems="center" spacing={1} textAlign="center">
      <Box component="img" src={ThemeStore.theme.popupsConfig?.welcomeGift?.image ?? profits} width={96} />
      <Typography fontSize={20} fontWeight={700}>
        {title}
      </Typography>
      {
          discountCode && (
            <Grid
              container
              justifyContent="space-between"
              alignItems="center"
              onClick={onCopy}
              width="100%"
              sx={{
                fontSize: '1.6rem',
                padding: (theme) => theme.spacing(0, 2),
                fontWeight: 400,
                height: '4rem',
                borderRadius: '6px',
                backgroundColor: '#f7f7f7',
                border: (theme) => `solid 1px ${theme.palette.primary.main}`,
              }}
            >
              {discountCode}
              {copied
                ? (
                  <CheckIcon
                    color="primary"
                    fontSize="large"
                    sx={{
                      width: '1.8rem',
                      cursor: 'pointer',
                    }}
                  />
                )
                : (
                  <ContentCopyIcon
                    color="primary"
                    sx={{
                      width: '1.8rem',
                      cursor: 'pointer',
                    }}
                  />
                )}
            </Grid>
          )
        }
      <Typography
        fontSize={14}
        fontWeight={500}
        sx={{
          opacity: 0.5,
          mb: (th) => `${th.spacing(2)} !important`,
        }}
      >
        {description}
      </Typography>
      <Button
        variant="contained"
        fullWidth
        onClick={() => CashbackPopupsStore.closeCashbackPopup()}
        sx={{
          fontSize: 16,
        }}
      >
        <Trans
          i18nKey="cashbackPopup.purchase.continue"
          defaults="Continue with my purchase"
        />
      </Button>
    </Stack>
  );
};

export default observer(Discount);
